import type { ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";

export interface WorkbenchSelectionTrayItem {
  id: string;
  label: ReactNode;
  meta?: ReactNode;
}

export interface WorkbenchSelectionTrayProps {
  title: ReactNode;
  description?: ReactNode;
  items: WorkbenchSelectionTrayItem[];
  onRemove?: (item: WorkbenchSelectionTrayItem) => void;
  removeLabel?: (item: WorkbenchSelectionTrayItem) => string;
  actions?: ReactNode;
  emptyState?: ReactNode;
  className?: string;
}

export function WorkbenchSelectionTray({
  title,
  description,
  items,
  onRemove,
  removeLabel = () => "Remove",
  actions,
  emptyState = null,
  className,
}: WorkbenchSelectionTrayProps) {
  return (
    <section
      data-monkeys-component="workbench-selection-tray"
      className={cn(
        "rounded-lg border border-border/70 bg-card p-4 text-card-foreground",
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {hasRenderableNode(description) ? (
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold tabular-nums text-primary">
          {items.length}
        </span>
      </div>
      {items.length ? (
        <ul className="mt-3 flex flex-wrap gap-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex max-w-full items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-foreground"
            >
              <span className="min-w-0 truncate font-medium">{item.label}</span>
              {hasRenderableNode(item.meta) ? (
                <span className="shrink-0 text-muted-foreground">{item.meta}</span>
              ) : null}
              {onRemove ? (
                <button
                  type="button"
                  aria-label={removeLabel(item)}
                  onClick={() => onRemove(item)}
                  className="rounded-full px-1 text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-3 rounded-md border border-dashed border-border px-4 py-5 text-center text-xs text-muted-foreground">
          {emptyState}
        </div>
      )}
      {hasRenderableNode(actions) ? (
        <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-border pt-4">
          {actions}
        </div>
      ) : null}
    </section>
  );
}
